// ============================================================================
// SessionForm — the schedule/log sheet. Used to:
//   • SCHEDULE a planned session (status=planned, scheduled_at)
//   • LOG a completed session (status=completed, performed_at)
//   • EDIT an existing session's details
// Fields: client (locked when pre-scoped), mode toggle, date/time picker,
// duration, location (saved quick-pick + Nominatim + free text), type, RPE,
// fee + paid, notes. Optional-first — only the client is required to create.
// Wires to useCreateSession / useUpdateSession / useDeleteSession; on create it
// navigates straight to the new session's log so the trainer keeps moving.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, SelectField, TextField } from "../../components";
import { IconCalendar, IconClock } from "../../components/icons";
import { useAccount } from "../../hooks/account";
import { useClient } from "../../hooks/clients";
import {
  useCreateSession,
  useDeleteSession,
  useUpdateSession,
} from "../../hooks/sessions";
import type { Client, Session, SessionInput } from "../../api";
import { useClientPackages } from "../../hooks/packages";
import { ClientPicker } from "./ClientPicker";
import { LocationPicker } from "./LocationPicker";
import type { LocationValue } from "./LocationPicker";
import { Sheet } from "./Sheet";
import {
  datetimeLocalToIso,
  defaultWhen,
  isoToDatetimeLocal,
  parseInt0,
  parseNum,
} from "./lib";
import {
  computeSessionFee,
  currencySymbol,
  formatMoney,
  isRateType,
  roundFee,
} from "../../lib/currency";
import { cn } from "../../lib/cn";

type Mode = "planned" | "completed";

interface SessionFormProps {
  open: boolean;
  onClose: () => void;
  /** Pre-scoped client (from ?client=) — locks the client picker. */
  lockedClient?: Client | null;
  /** When set, the form edits this session instead of creating one. */
  session?: Session | null;
}

const DURATION_PRESETS = [30, 45, 60, 90];

export function SessionForm({
  open,
  onClose,
  lockedClient,
  session,
}: SessionFormProps) {
  const { t } = useTranslation("sessions");
  const { t: tp } = useTranslation("packages");
  const navigate = useNavigate();
  const account = useAccount();

  const createSession = useCreateSession();
  const updateSession = useUpdateSession();
  const deleteSession = useDeleteSession();

  const editing = Boolean(session);

  // --- form state -----------------------------------------------------------
  const [clientId, setClientId] = useState<number | null>(null);
  // The full client picked in the form (carries the charge model). Locked /
  // edited sessions resolve their client via useClient below instead.
  const [pickedClient, setPickedClient] = useState<Client | null>(null);
  const [mode, setMode] = useState<Mode>("planned");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState("");
  const [location, setLocation] = useState<LocationValue>({
    location_id: null,
    location_text: null,
  });
  const [type, setType] = useState("");
  const [rpe, setRpe] = useState("");
  const [fee, setFee] = useState("");
  // Once the trainer edits the fee, stop auto-filling it from the rate.
  const [feeOverridden, setFeeOverridden] = useState(false);
  const [paid, setPaid] = useState(false);
  const [notes, setNotes] = useState("");
  const [packageId, setPackageId] = useState<number | null>(null);
  const [clientError, setClientError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();

  // (Re)seed the form whenever it opens or the edited session changes.
  useEffect(() => {
    if (!open) return;
    setClientError(undefined);
    setSubmitError(undefined);
    if (session) {
      setClientId(session.client_id);
      setPickedClient(null);
      setMode(session.status);
      setWhen(
        isoToDatetimeLocal(session.performed_at ?? session.scheduled_at) ||
          defaultWhen(),
      );
      setDuration(session.duration_min != null ? String(session.duration_min) : "");
      setLocation({
        location_id: session.location_id,
        location_text: session.location_text,
      });
      setType(session.type ?? "");
      setRpe(session.session_rpe != null ? String(session.session_rpe) : "");
      setFee(session.fee_amount != null ? String(session.fee_amount) : "");
      // An existing stored fee is treated as the trainer's number — don't
      // auto-overwrite it from the rate; a blank one can still auto-fill.
      setFeeOverridden(session.fee_amount != null);
      setPaid(session.paid === 1);
      setNotes(session.notes ?? "");
      setPackageId(session.package_id ?? null);
    } else {
      setClientId(lockedClient?.id ?? null);
      setPickedClient(null);
      setMode("planned");
      setWhen(defaultWhen());
      setDuration("");
      setLocation({ location_id: null, location_text: null });
      setType("");
      setRpe("");
      setFee("");
      setFeeOverridden(false);
      setPaid(false);
      setNotes("");
      setPackageId(null);
    }
  }, [open, session, lockedClient]);

  // Resolve the client whose charge model auto-fills the fee. Prefer the locked
  // client, then the one picked in the form; otherwise fetch by id (covers
  // editing an existing session, where we only know client_id).
  const needFetch =
    clientId != null &&
    lockedClient?.id !== clientId &&
    pickedClient?.id !== clientId;
  const fetchedClientQuery = useClient(needFetch ? clientId : undefined);
  const rateClient: Client | null =
    (lockedClient?.id === clientId ? lockedClient : null) ??
    (pickedClient?.id === clientId ? pickedClient : null) ??
    (fetchedClientQuery.data?.id === clientId ? fetchedClientQuery.data : null);

  // Packages query — active packages for the chosen client drive the package
  // select and amortized fee fill. Enabled only when we have a client.
  const packagesQuery = useClientPackages(clientId ?? 0);
  const activePackages = (packagesQuery.data ?? []).filter(
    (p) => p.status === "active" && p.remaining > 0,
  );

  // Default a brand-new session to the client's first active package.
  useEffect(() => {
    if (session) return; // editing keeps the stored link
    if (packageId == null && activePackages.length > 0) {
      setPackageId(activePackages[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePackages.length, session]);

  const selectedPackage = activePackages.find((p) => p.id === packageId) ?? null;

  const currency = account.data?.default_currency?.trim() || "";
  const symbol = currencySymbol(currency);

  // Live auto-fee from the client's charge model. per_session = flat amount;
  // per_hour = rate × (duration / 60), recomputed as the duration changes.
  const durationMin = parseInt0(duration);
  const autoFee = useMemo(() => {
    if (!rateClient || !isRateType(rateClient.rate_type)) return null;
    const f = computeSessionFee(
      rateClient.rate_type,
      rateClient.rate_amount,
      null,
      durationMin,
    );
    return f == null ? null : roundFee(f);
  }, [rateClient, durationMin]);

  // While the trainer hasn't overridden the fee, keep the field tracking the
  // auto-computed value. When a package is selected, seed from its per-lesson
  // value instead of the client's rate (amortized fee, per plan spec).
  useEffect(() => {
    if (feeOverridden) return;
    if (selectedPackage?.per_lesson != null) {
      setFee(String(selectedPackage.per_lesson));
      return;
    }
    setFee(autoFee != null ? String(autoFee) : "");
  }, [autoFee, feeOverridden, selectedPackage]);

  const hasRate = rateClient != null && isRateType(rateClient.rate_type);
  const rateHint = hasRate
    ? rateClient!.rate_type === "per_hour"
      ? t("form.feeAutoHourly", {
          rate: formatMoney(rateClient!.rate_amount, currency, { symbol }),
        })
      : t("form.feeAutoSession", {
          rate: formatMoney(rateClient!.rate_amount, currency, { symbol }),
        })
    : undefined;
  // The fee field DISPLAY follows the SELECTED PACKAGE when one is chosen — its
  // own currency symbol + amortized per-lesson value — else the trainer's
  // default currency + the client's rate. The submitted body already sends the
  // package currency; this stops the on-screen symbol/hint from misleading
  // (e.g. a HKD package must render HK$ 400, not the trainer's default £ 400).
  const effectiveSymbol = selectedPackage
    ? selectedPackage.currency_symbol ?? currencySymbol(selectedPackage.currency)
    : symbol;
  const effectiveCurrency = selectedPackage ? selectedPackage.currency : currency;
  const autoValue = selectedPackage?.per_lesson ?? autoFee;
  const autoHint = selectedPackage
    ? selectedPackage.per_lesson != null
      ? tp("perLessonHint", {
          value: formatMoney(selectedPackage.per_lesson, effectiveCurrency, {
            symbol: effectiveSymbol,
          }),
        })
      : undefined
    : rateHint;
  const busy =
    createSession.isPending || updateSession.isPending || deleteSession.isPending;

  const buildBody = (): SessionInput => {
    const iso = datetimeLocalToIso(when);
    const body: SessionInput = {
      status: mode,
      duration_min: parseInt0(duration),
      location_id: location.location_id,
      location_text: location.location_text?.trim() || null,
      type: type.trim() || null,
      session_rpe: parseNum(rpe),
      fee_amount: parseNum(fee),
      // When a package is selected FE is the sole driver of currency + paid:
      // the backend's setdefault is a no-op once we send a non-None value, so
      // we must send the package's own currency and mark the session as prepaid.
      currency: selectedPackage ? selectedPackage.currency : (currency || null),
      paid: selectedPackage ? true : paid,
      notes: notes.trim() || null,
      package_id: packageId,
    };
    // The backend reads `scheduled_at` for planned and `performed_at` for
    // completed; send the relevant one and clear the other.
    if (mode === "completed") {
      body.performed_at = iso;
      body.scheduled_at = null;
    } else {
      body.scheduled_at = iso;
      body.performed_at = null;
    }
    return body;
  };

  const onSubmit = () => {
    setSubmitError(undefined);
    if (clientId == null) {
      setClientError(t("form.clientRequired"));
      return;
    }
    const body = buildBody();
    if (editing && session) {
      updateSession.mutate(
        { sessionId: session.id, body },
        {
          onSuccess: () => onClose(),
          onError: () => setSubmitError(t("form.saveError")),
        },
      );
    } else {
      createSession.mutate(
        { clientId, body },
        {
          onSuccess: (created) => {
            // Honor the backend's package_full flag — the link was suppressed
            // because the package ran out; warn the trainer inline.
            if ((created as unknown as { package_full?: boolean }).package_full) {
              setSubmitError(tp("full"));
              return;
            }
            onClose();
            navigate(`/app/sessions/${created.id}`);
          },
          onError: () => setSubmitError(t("form.saveError")),
        },
      );
    }
  };

  const onDelete = () => {
    if (!session) return;
    if (!window.confirm(t("form.deleteConfirm"))) return;
    deleteSession.mutate(session.id, {
      onSuccess: () => {
        onClose();
        navigate("/app/sessions");
      },
    });
  };

  const title = editing
    ? t("form.editTitle")
    : mode === "completed"
      ? t("form.logTitle")
      : t("form.scheduleTitle");

  const durationPresetActive = useMemo(
    () => DURATION_PRESETS.find((d) => String(d) === duration.trim()),
    [duration],
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <div className="flex items-center gap-3">
          {editing && (
            <Button
              variant="ghost"
              onClick={onDelete}
              disabled={busy}
              className="!text-danger"
            >
              {t("form.delete")}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t("form.cancel")}
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy
              ? t("form.saving")
              : editing
                ? t("form.save")
                : t("form.create")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 py-1">
        {/* Mode toggle */}
        <div>
          <p className="mb-1.5 font-display text-sm font-bold text-body">
            {t("form.mode")}
          </p>
          <div className="grid grid-cols-2 gap-1 rounded-pill bg-surface-sunken p-1">
            <ModeBtn
              active={mode === "planned"}
              onClick={() => setMode("planned")}
              icon={<IconCalendar width={16} height={16} />}
            >
              {t("form.modePlanned")}
            </ModeBtn>
            <ModeBtn
              active={mode === "completed"}
              onClick={() => setMode("completed")}
              icon={<IconClock width={16} height={16} />}
            >
              {t("form.modeCompleted")}
            </ModeBtn>
          </div>
        </div>

        {/* Client */}
        <div>
          <p className="mb-1.5 font-display text-sm font-bold text-body">
            {t("form.client")}
            <span className="ml-1 text-ember">*</span>
          </p>
          {lockedClient ? (
            <div className="flex items-center gap-2 rounded-md bg-coral-soft px-4 py-3 ring-1 ring-inset ring-coral/30">
              <span className="font-display font-bold text-body">
                {lockedClient.name}
              </span>
            </div>
          ) : (
            <ClientPicker
              value={clientId}
              onChange={(id, client) => {
                setClientId(id);
                setPickedClient(client);
                // Re-arm auto-fill for the newly chosen client's rate.
                setFeeOverridden(false);
                setClientError(undefined);
              }}
              error={clientError}
            />
          )}
        </div>

        {/* Date & time */}
        <DateTimeField
          label={t("form.when")}
          value={when}
          onChange={setWhen}
        />

        {/* Duration */}
        <div>
          <p className="mb-1.5 font-display text-sm font-bold text-body">
            {t("form.duration")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {DURATION_PRESETS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDuration(String(d))}
                aria-pressed={durationPresetActive === d}
                className={cn(
                  "rounded-pill px-4 py-2 text-sm font-bold transition-all duration-fast",
                  durationPresetActive === d
                    ? "bg-grad-coral text-white shadow-volt"
                    : "bg-surface-sunken text-body-muted ring-1 ring-inset ring-line hover:bg-line",
                )}
              >
                {d}
              </button>
            ))}
            <div className="w-28">
              <TextField
                label={t("form.duration")}
                hideLabel
                type="number"
                inputMode="numeric"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                suffix={t("form.minutes")}
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div>
          <p className="mb-1.5 font-display text-sm font-bold text-body">
            {t("form.location")}
          </p>
          <LocationPicker value={location} onChange={setLocation} />
        </div>

        {/* Type + RPE */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={t("form.type")}
            placeholder={t("form.typePlaceholder")}
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
          <SelectField
            label={t("form.rpe")}
            hint={t("form.rpeHint")}
            placeholder="—"
            value={rpe}
            onChange={(e) => setRpe(e.target.value)}
            options={Array.from({ length: 10 }, (_, i) => ({
              label: String(i + 1),
              value: String(i + 1),
            }))}
          />
        </div>

        {/* Package — FE is the sole driver: explicit null = ad-hoc even when the
            client has an active package. Auto-seeds to the client's first active
            package on a new session; editing sessions load the stored link. */}
        {activePackages.length > 0 && (
          <SelectField
            label={tp("selectPackage")}
            value={packageId == null ? "" : String(packageId)}
            onChange={(e) => {
              const v = e.target.value;
              setPackageId(v === "" ? null : Number(v));
              setFeeOverridden(false);
            }}
            options={[
              { value: "", label: tp("noPackage") },
              ...activePackages.map((p) => ({
                value: String(p.id),
                label: `${p.name || tp("title")} — ${p.remaining} ${tp("remaining", { count: p.remaining })}`,
              })),
            ]}
          />
        )}

        {/* Fee + paid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <TextField
              label={t("form.fee")}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={fee}
              onChange={(e) => {
                setFee(e.target.value);
                setFeeOverridden(true);
              }}
              hint={!feeOverridden ? autoHint : undefined}
              suffix={effectiveSymbol || undefined}
            />
            {feeOverridden && autoValue != null && (
              <button
                type="button"
                onClick={() => setFeeOverridden(false)}
                className="mt-1.5 text-sm font-bold text-coral transition-colors hover:text-coral-600 focus-visible:outline-none focus-visible:underline"
              >
                {t("form.feeResetAuto", {
                  amount: formatMoney(autoValue, effectiveCurrency, {
                    symbol: effectiveSymbol,
                  }),
                })}
              </button>
            )}
          </div>
          <div className="flex items-end">
            <button
              type="button"
              role="switch"
              aria-checked={paid}
              onClick={() => setPaid((p) => !p)}
              className={cn(
                "flex h-12 w-full items-center justify-between rounded-md px-4 font-display font-bold transition-colors duration-fast ring-1 ring-inset",
                paid
                  ? "bg-lime-soft text-lime-600 ring-lime/40"
                  : "bg-surface text-body-soft ring-line-strong",
              )}
            >
              {paid ? t("form.paid") : t("form.notPaid")}
              <span
                className={cn(
                  "relative h-6 w-11 rounded-full transition-colors duration-fast",
                  paid ? "bg-lime" : "bg-line-strong",
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all duration-fast",
                    paid ? "left-[1.375rem]" : "left-0.5",
                  )}
                />
              </span>
            </button>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label
            htmlFor="session-notes"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("form.notes")}
          </label>
          <textarea
            id="session-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t("form.notesPlaceholder")}
            className="w-full rounded-md bg-surface px-4 py-3 text-base text-body ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast placeholder:text-body-faint focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>

        {submitError && (
          <p className="text-sm font-medium text-danger" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Sheet>
  );
}

function ModeBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-pill px-3 py-2.5 font-display text-sm font-bold transition-all duration-fast",
        active ? "bg-surface text-body shadow-xs" : "text-body-soft hover:text-body",
      )}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
}

/** A native datetime-local input styled to match the system fields. */
function DateTimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label
        htmlFor="session-when"
        className="mb-1.5 block font-display text-sm font-bold text-body"
      >
        {label}
      </label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-body-faint">
          <IconCalendar width={18} height={18} />
        </span>
        <input
          id="session-when"
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 w-full rounded-md bg-surface pl-10 pr-4 text-base text-body ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
        />
      </div>
    </div>
  );
}
